<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import Swal from 'sweetalert2'
import { fetchEmpresaConfig, saveEmpresaConfig, clearEmpresaCache } from './services/empresa.api'

const loading = ref(true)
const saving = ref(false)
const logoFile = ref(null)
const preview = ref(null)
const currentLogoUrl = ref(null)

const form = ref({
  nombre: '',
  slogan: '',
  ruc: '',
  representante: '',
  direccion: '',
  telefono: '',
  email: '',
})

const iniciales = computed(() => {
  const n = form.value.nombre?.trim() || 'E'
  return n.split(/\s+/).slice(0,2).map(p=>p[0]?.toUpperCase()).join('') || 'E'
})

const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp']
const MAX_FILE_SIZE = 2 * 1024 * 1024 // 2 MB

function validateImageFile(file) {
  if (!ALLOWED_MIME.includes(file.type)) {
    Swal.fire('Archivo no válido', 'Formato permitido: JPG, PNG o WEBP.', 'warning')
    return false
  }
  if (file.size > MAX_FILE_SIZE) {
    Swal.fire('Archivo muy grande', 'El tamaño máximo es 2 MB.', 'warning')
    return false
  }
  return true
}

function revokePreview() {
  if (preview.value && preview.value.startsWith('blob:')) {
    URL.revokeObjectURL(preview.value)
  }
}

function onFile(e) {
  const f = e.target.files?.[0]
  if (f && !validateImageFile(f)) {
    e.target.value = ''
    logoFile.value = null
    revokePreview()
    preview.value = null
    return
  }
  revokePreview()
  logoFile.value = f || null
  preview.value = f ? URL.createObjectURL(f) : null
}

onBeforeUnmount(() => {
  revokePreview()
})

async function cargar() {
  try {
    loading.value = true
    const data = await fetchEmpresaConfig()
    form.value.nombre = data.nombre || data.nombre_empresa || ''
    form.value.slogan = data.slogan || ''
    form.value.ruc = data.ruc || data.identificacion || ''
    form.value.representante = data.representante || ''
    form.value.direccion = data.direccion || ''
    form.value.telefono = data.telefono || ''
    form.value.email = data.email || ''
    currentLogoUrl.value = data.logo_url || data.logo || null
  } catch (e) {
    // si 404 es primera vez, dejamos formulario vacío
    if (e.response?.status !== 404) {
      Swal.fire('Aviso', e.response?.data?.message || 'No se pudo cargar configuración', 'warning')
    }
  } finally {
    loading.value = false
  }
}

async function guardar() {
  if (!form.value.nombre?.trim()) {
    Swal.fire('Validación', 'Nombre de empresa es obligatorio', 'warning')
    return
  }
  if (form.value.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email)) {
    Swal.fire('Validación', 'Email no válido', 'warning')
    return
  }
  try {
    saving.value = true
    const saved = await saveEmpresaConfig(form.value, logoFile.value)
    clearEmpresaCache()
    // actualizar preview con lo devuelto
    currentLogoUrl.value = saved.logo_url || saved.logo || currentLogoUrl.value
    logoFile.value = null
    preview.value = null
    Swal.fire('Guardado', 'Configuración de empresa actualizada', 'success')
  } catch (e) {
    Swal.fire('Error', e.response?.data?.message || e.message || 'No se pudo guardar', 'error')
  } finally {
    saving.value = false
  }
}

onMounted(cargar)
</script>

<template>
  <div class="container-fluid py-4">
    <div class="card p-4" style="max-width: 900px; margin: 0 auto;">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h5 class="mb-0">Configuración de Empresa</h5>
          <small class="text-muted">Único registro — se usa en recibos, encabezados y reportes</small>
        </div>
        <span class="badge bg-primary">Single Record</span>
      </div>

      <div v-if="loading" class="text-center py-4 text-muted">
        <i class="fa fa-spinner fa-spin me-2"></i> Cargando...
      </div>

      <form v-else @submit.prevent="guardar">
        <div class="row g-3">
          <div class="col-12 col-md-6">
            <label class="form-label fw-bold">Nombre de Empresa *</label>
            <input v-model="form.nombre" type="text" class="form-control" placeholder="Ej: Vendetta Fitness" />
          </div>
          <div class="col-12 col-md-6">
            <label class="form-label fw-bold">Slogan</label>
            <input v-model="form.slogan" type="text" class="form-control" placeholder="Ej: teVaAdolerPeroTeVaAgustar" />
          </div>

          <div class="col-12 col-md-6">
            <label class="form-label fw-bold">RUC / Identificación</label>
            <input v-model="form.ruc" type="text" class="form-control" placeholder="Ej: 1790000000001" />
          </div>
          <div class="col-12 col-md-6">
            <label class="form-label fw-bold">Representante</label>
            <input v-model="form.representante" type="text" class="form-control" placeholder="Ej: Juan Pérez" />
          </div>

          <div class="col-12 col-md-6">
            <label class="form-label fw-bold">Teléfono</label>
            <input v-model="form.telefono" type="text" class="form-control" />
          </div>
          <div class="col-12 col-md-6">
            <label class="form-label fw-bold">Email</label>
            <input v-model="form.email" type="email" class="form-control" />
          </div>

          <div class="col-12">
            <label class="form-label fw-bold">Dirección</label>
            <input v-model="form.direccion" type="text" class="form-control" placeholder="Calle principal y secundaria" />
          </div>

          <div class="col-12">
            <label class="form-label fw-bold">Logo</label>
            <div class="d-flex align-items-center gap-3 mb-2">
              <div class="logo-preview">
                <img v-if="preview" :src="preview" alt="preview" />
                <img v-else-if="currentLogoUrl" :src="currentLogoUrl" alt="logo actual" />
                <div v-else class="fallback">{{ iniciales }}</div>
              </div>
              <div>
                <input type="file" class="form-control" accept="image/jpeg,image/png,image/webp" @change="onFile" />
                <small class="text-muted">JPG, PNG, WEBP máx. 2 MB. Se usa en recibo y encabezados.</small>
              </div>
            </div>
          </div>
        </div>

        <!-- Preview recibo header -->
        <div class="mt-4 p-3 rounded" style="background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%); color:#fff;">
          <div class="d-flex align-items-center gap-3">
            <img v-if="preview" :src="preview" style="height:48px" alt="logo" />
            <img v-else-if="currentLogoUrl" :src="currentLogoUrl" style="height:48px" alt="logo" />
            <div v-else class="bg-white text-dark rounded px-2 py-1 fw-bold">{{ iniciales }}</div>
            <div>
              <div class="fw-bold text-uppercase" style="letter-spacing:1px">{{ form.nombre || 'Nombre Empresa' }}</div>
              <div style="font-family: 'Caveat', cursive; font-size:1rem">{{ form.slogan || 'slogan' }}</div>
              <small v-if="form.ruc" class="opacity-75">RUC: {{ form.ruc }}</small>
            </div>
          </div>
        </div>

        <div class="text-end mt-4">
          <button type="submit" class="btn btn-success" :disabled="saving">
            <i v-if="saving" class="fa fa-spinner fa-spin me-1"></i> Guardar Cambios
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
.logo-preview {
  width: 72px; height: 72px; border-radius: 10px; overflow: hidden;
  background: #f0f2f5; display:grid; place-items:center; flex-shrink:0;
  border: 1px solid #dee2e6;
}
.logo-preview img { width:100%; height:100%; object-fit: contain; background:#fff; }
.fallback { font-weight:700; color:#344767; }
</style>
